import React, { useEffect, useState, useCallback } from 'react';
import {
  Bell, Send, Trash2, X,
  ChevronLeft, ChevronRight,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { notificationsAPI } from '../../api/notifications';
import PageHeader from '../../components/ui/PageHeader';
import Button     from '../../components/ui/Button';
import Input      from '../../components/ui/Input';

// ── Helpers ──────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const PER_PAGE = 10;

// ── Skeleton ─────────────────────────────────────────────────
function NotifSkeleton() {
  return (
    <div className="card flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="skeleton h-3.5 w-40 rounded" />
        <div className="skeleton h-6 w-6 flex-shrink-0 rounded-lg" />
      </div>
      <div className="skeleton h-3 w-full rounded" />
      <div className="skeleton h-3 w-3/4 rounded" />
      <div className="skeleton h-3 w-20 rounded mt-1" />
    </div>
  );
}

// ── Delete confirm sheet ──────────────────────────────────────
function DeleteSheet({ notif, onClose, onConfirm, deleting }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!deleting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                 style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <Trash2 size={16} style={{ color: '#fca5a5' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">Delete Notification</h3>
              <p className="text-xs mt-0.5 truncate max-w-[200px]"
                 style={{ color: 'rgba(255,255,255,0.4)' }}>
                {notif.title}
              </p>
            </div>
          </div>
          {!deleting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        <p className="mb-5 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          This notification will be permanently removed from all users' dashboards.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 rounded-2xl py-3.5 text-sm font-semibold
                       transition-all active:scale-95 disabled:opacity-50"
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl
                       py-3.5 text-sm font-semibold transition-all active:scale-95
                       disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)',
              color: '#fff',
            }}
          >
            {deleting
              ? <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              : <><Trash2 size={14} /> Delete</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Notification card ─────────────────────────────────────────
function NotifCard({ notif, onDelete }) {
  return (
    <div className="card flex flex-col gap-2">
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg mt-0.5"
             style={{ background: 'rgba(212,160,23,0.15)' }}>
          <Bell size={13} style={{ color: '#d4a017' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-snug">
            {notif.title}
          </p>
          <p className="text-xs mt-1 leading-relaxed"
             style={{ color: 'rgba(255,255,255,0.55)' }}>
            {notif.description}
          </p>
          <p className="text-[11px] mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {formatDate(notif.created_at)}
          </p>
        </div>
        <button
          onClick={() => onDelete(notif)}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl
                     transition-all active:scale-90 mt-0.5"
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.2)',
          }}
          aria-label="Delete notification"
        >
          <Trash2 size={13} style={{ color: '#fca5a5' }} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function NotificationsPage() {
  // list state
  const [notifs,     setNotifs]     = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [listError,  setListError]  = useState('');

  // compose form state
  const [form,       setForm]       = useState({ title: '', description: '' });
  const [errors,     setErrors]     = useState({});
  const [sending,    setSending]    = useState(false);
  const [formError,  setFormError]  = useState('');

  // delete state
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // toast
  const [toast, setToast] = useState(null); // { type, message }

  // ── GET /api/admin/notifications ─────────────────────────
  const fetchNotifs = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const { data } = await notificationsAPI.getList({ page, per_page: PER_PAGE });
      if (data.status) {
        setNotifs(data.data.notifications);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setListError(err?.response?.data?.message || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Form handlers ─────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (formError)    setFormError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim())       errs.title       = 'Title is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    return errs;
  };

  // ── POST /api/admin/notifications ────────────────────────
  const handleSend = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSending(true);
    setFormError('');
    try {
      const { data } = await notificationsAPI.send({
        title:       form.title.trim(),
        description: form.description.trim(),
      });
      if (data.status) {
        // Prepend to list if on page 1, else refresh
        if (page === 1) {
          setNotifs((prev) => [data.data, ...prev]);
          setPagination((prev) =>
            prev ? { ...prev, total: prev.total + 1 } : prev
          );
        } else {
          setPage(1);
        }
        setForm({ title: '', description: '' });
        setToast({ type: 'success', message: 'Notification sent successfully' });
      } else {
        setFormError(data.message || 'Failed to send notification.');
      }
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.title?.[0] ||
        err?.response?.data?.errors?.description?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  // ── DELETE /api/admin/notifications/{id} ─────────────────
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const { data } = await notificationsAPI.remove(toDelete.id);
      if (data.status) {
        setNotifs((prev) => prev.filter((n) => n.id !== toDelete.id));
        setPagination((prev) =>
          prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev
        );
        setToast({ type: 'success', message: 'Notification deleted successfully' });
        setToDelete(null);
      }
    } catch (err) {
      setToast({
        type: 'error',
        message: err?.response?.data?.message || 'Failed to delete notification.',
      });
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader title="Notifications" />

      <div className="flex flex-col gap-4 p-4">

        {/* ── Toast ── */}
        {toast && (
          <div
            className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
            style={{
              background: toast.type === 'success' ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.12)',
              border: `1px solid ${toast.type === 'success' ? 'rgba(5,150,105,0.35)' : 'rgba(239,68,68,0.3)'}`,
              color: toast.type === 'success' ? '#6ee7b7' : '#fca5a5',
            }}
          >
            {toast.type === 'success'
              ? <CheckCircle2 size={15} className="flex-shrink-0" />
              : <AlertCircle  size={15} className="flex-shrink-0" />}
            <span>{toast.message}</span>
          </div>
        )}

        {/* ── Compose form (API #17) ── */}
        <div className="card flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.15)' }}>
              <Send size={13} style={{ color: '#d4a017' }} />
            </div>
            <h3 className="font-serif text-sm font-semibold text-white">
              Send Notification
            </h3>
          </div>

          {formError && (
            <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
                 style={{
                   background: 'rgba(239,68,68,0.12)',
                   border: '1px solid rgba(239,68,68,0.3)',
                   color: '#fca5a5',
                 }}>
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <form onSubmit={handleSend} noValidate className="flex flex-col gap-4">

            <Input
              label="Title"
              type="text"
              name="title"
              placeholder="e.g. Special Offer This Month"
              value={form.title}
              onChange={handleChange}
              error={errors.title}
              maxLength={255}
            />

            <div className="flex flex-col gap-1.5">
              <label className="input-label">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="e.g. Invest ₹50,000 or more this month and get 2% extra profit..."
                rows={3}
                className={`input-field resize-none ${errors.description ? 'border-red-400' : ''}`}
              />
              {errors.description && (
                <p className="text-xs" style={{ color: '#fca5a5' }}>{errors.description}</p>
              )}
            </div>

            <Button type="submit" variant="gold" fullWidth loading={sending}>
              <Send size={15} />
              Send to All Users
            </Button>

          </form>
        </div>

        {/* ── Divider ── */}
        <div className="divider">Sent Notifications</div>

        {/* ── Summary ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {pagination.total} notification{pagination.total !== 1 ? 's' : ''} sent
          </p>
        )}

        {/* ── List error ── */}
        {listError && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>
            {listError}
          </div>
        )}

        {/* ── Loading skeletons (API #18) ── */}
        {loading && !listError && (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => <NotifSkeleton key={i} />)}
          </div>
        )}

        {/* ── Notifications list (API #18) ── */}
        {!loading && !listError && notifs.length > 0 && (
          <div className="flex flex-col gap-3">
            {notifs.map((n) => (
              <NotifCard key={n.id} notif={n} onDelete={setToDelete} />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !listError && notifs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <Bell size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              No notifications sent yet
            </p>
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>

      {/* ── Delete confirmation sheet (API #19) ── */}
      {toDelete && (
        <DeleteSheet
          notif={toDelete}
          onClose={() => !deleting && setToDelete(null)}
          onConfirm={handleDeleteConfirm}
          deleting={deleting}
        />
      )}
    </div>
  );
}
